import React, { useState, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import * as S from './SelectList.styles';
import Input from '../Input/Input';
import P from '../Texts/P/P';
import OutsideAlerter from '../OutsideAlerter/OutsideAlerter';
import Icon from '../Icon/Icon';
import {
  BACKSPACE_KEY,
  DELETE_KEY,
  NULL_OPTION_SELECT,
} from '../../utils/Constants';

const SelectList = (props) => {
  const {
    placeholder,
    options,
    label,
    onChange,
    disabled,
    inputStatus,
    message,
    onFocus,
    onBlur,
    name,
    value,
  } = props;
  const [filteredOptions, setFilteredOptions] = useState([]);
  const [open, setOpen] = useState(false);
  const [showPlaceHolder, setShowPlaceHolder] = useState(!!placeholder);
  const inputRef = useRef(null);
  const valueRef = useRef(null);
  const [selectedValue, setSelectedValue] = useState(NULL_OPTION_SELECT);
  const [showDisplay, setShowDisplay] = useState(false);

  useEffect(() => {
    setSelectedValue(
      options.find((elem) => elem.value === value) ?? NULL_OPTION_SELECT
    );
  }, [value]);

  useEffect(() => {
    setShowDisplay(selectedValue && selectedValue !== NULL_OPTION_SELECT);
  }, [selectedValue]);

  useEffect(() => {
    if (open) {
      inputRef.current.focus();
    }
  }, [open]);

  useEffect(() => {
    if (filteredOptions !== options) {
      setFilteredOptions(options || []);
    }
  }, [options]);

  const filterOptionsByLabel = (valueEvent) => {
    return valueEvent
      ? options.filter(({ label: item }) =>
          item.toLowerCase().includes(valueEvent.toLowerCase())
        )
      : options;
  };

  const handleClose = () => {
    setOpen(false);
  };

  const handleOnChange = (event) => {
    const newOptions = filterOptionsByLabel(event.target.value);
    setFilteredOptions(newOptions);
    setSelectedValue({ value: event.target.value, label: event.target.value });
  };

  const handleReset = () => {
    onChange && onChange('');
    setShowPlaceHolder(true);
    setFilteredOptions(options);
    setSelectedValue(NULL_OPTION_SELECT);
    handleClose();
  };

  const handleSelectOption = (option) => {
    const optionChosen = options.filter(
      ({ value: item }) => item === option.value
    );
    setFilteredOptions(optionChosen);
    setSelectedValue(option);
    onChange && onChange(option.value);
    handleClose();
    valueRef.current.focus();
  };

  const handleOnFocus = () => {
    onFocus && onFocus();
    setOpen(true);
    setShowPlaceHolder(false);
  };

  const handleOnBlur = () => {
    onBlur && onBlur();
    setShowPlaceHolder(inputRef.current.value.length === 0);
  };

  const handleKeyDown = (event) => {
    event.preventDefault();
    if (event.key === BACKSPACE_KEY || event.key === DELETE_KEY) {
      handleReset();
    }
  };

  const renderFilteredOptions = () => (
    <OutsideAlerter
      handleOutSideClick={handleReset}
      active={open}
      exclude={['#select-list']}
    >
      <S.DataResult>
        {filteredOptions.length > 0 &&
          filteredOptions.map((filteredOption, key) => (
            <S.DataItem
              data-testid={`option-${key}`}
              key={key}
              onClick={() => handleSelectOption(filteredOption)}
            >
              <S.DataItemText>{filteredOption.label}</S.DataItemText>
            </S.DataItem>
          ))}
      </S.DataResult>
    </OutsideAlerter>
  );

  return (
    <>
      <S.Wrapper data-testid='select-list'>
        <S.InputWrapper showDisplay={showDisplay}>
          <S.InputDisplay
            tabIndex={-1}
            onKeyDown={handleKeyDown}
            ref={valueRef}
            hasLabel={!!label}
            data-testid='input-display'
          >
            <P type='2' variant='neutral' modifier='strong'>
              {selectedValue.label}
            </P>
            {selectedValue.value && (
              <Icon
                name='cancel_outline'
                onClick={handleReset}
                modifier='strong'
                cursor='pointer'
              />
            )}
          </S.InputDisplay>
          <Input
            disabled={disabled}
            inputRef={inputRef}
            id='select-list'
            type='text'
            label={label}
            placeHolder={showPlaceHolder ? placeholder : ''}
            onChange={handleOnChange}
            value={selectedValue.value}
            autocomplete='off'
            icons={{
              iconLeft: 'search_outline',
            }}
            inputStatus={inputStatus}
            message={message}
            onFocus={handleOnFocus}
            onBlur={handleOnBlur}
            name={name}
          />
        </S.InputWrapper>
        {open && renderFilteredOptions()}
      </S.Wrapper>
    </>
  );
};

SelectList.defaultProps = {
  options: [],
  placeholder: '',
  label: '',
  disabled: false,
  inputStatus: 'normal',
};

SelectList.propTypes = {
  disabled: PropTypes.bool,
  placeholder: PropTypes.string,
  label: PropTypes.string,
  options: PropTypes.array,
  onChange: PropTypes.func,
  onInput: PropTypes.func,
  onFocus: PropTypes.func,
  onBlur: PropTypes.func,
  inputStatus: PropTypes.oneOf(['normal', 'error']),
  message: PropTypes.string,
};

export default SelectList;
